import { useState } from 'react'
import { toast } from 'react-toastify'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import AppBadge from 'shared/badges/app-badge'
import {
  useDocumentExemplars,
  useUploadDocumentExemplar,
  useCompleteDocumentExemplar,
  useDestroyDocumentExemplar,
  useExtractDocumentExemplar
} from 'api/hooks/use-document-exemplars'

const RETRYABLE = ['uploaded', 'failed', 'extracted']

const STATUS_VARIANT = {
  completed: 'success',
  extracted: 'info',
  extracting: 'warning',
  uploaded: 'info',
  pending: 'info',
  failed: 'danger'
}

const anyExtracting = (exemplars) => exemplars.some((e) => e.status === 'extracting')

// Upload past audit reports and review the blueprints extracted from them. Accepting ("Use")
// a blueprint makes it available to the agent when generating new document reports.
const DocumentExemplars = () => {
  const [name, setName] = useState('')
  const [file, setFile] = useState(null)

  const { data, isLoading, isError } = useDocumentExemplars({
    options: { refetchInterval: (queryData) => (anyExtracting(queryData?.document_exemplars || []) ? 5000 : false) }
  })
  const exemplars = data?.document_exemplars || []

  const { mutate: upload, isLoading: isUploading } = useUploadDocumentExemplar({
    options: {
      onSuccess: () => {
        toast.success('Report uploaded — extracting its blueprint')
        setName('')
        setFile(null)
      },
      onError: () => toast.error('Upload failed. Please check the PDF and try again.')
    }
  })

  const { mutate: accept } = useCompleteDocumentExemplar({
    options: { onSuccess: () => toast.success('Blueprint is now available for generation') }
  })
  const { mutate: remove } = useDestroyDocumentExemplar({
    options: { onSuccess: () => toast.success('Exemplar deleted') }
  })
  const { mutate: retry } = useExtractDocumentExemplar({
    options: {
      onSuccess: () => toast.success('Re-extracting the blueprint'),
      onError: () => toast.error('Could not retry extraction')
    }
  })

  const handleUpload = (e) => {
    e.preventDefault()
    if (!name.trim() || !file) return
    upload({ name: name.trim(), file })
  }

  return (
    <Row>
      <Col lg={4} className="mb-4">
        <Card>
          <Card.Body>
            <Card.Title className="h6">Upload a past report</Card.Title>
            <p className="text-muted small">
              We extract its structure and tone into a reusable blueprint for generating new reports.
            </p>
            <Form onSubmit={handleUpload}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="FY24 PCard Audit"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>PDF</Form.Label>
                <Form.Control
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Form.Group>
              <Button type="submit" variant="primary" disabled={!name.trim() || !file || isUploading}>
                {isUploading ? 'Uploading…' : 'Upload'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Col>
      <Col lg={8}>
        <Card>
          <Card.Body>
            <Card.Title className="h6">Uploaded reports</Card.Title>
            {isLoading && <p className="text-muted small mb-0">Loading…</p>}
            {isError && <p className="text-danger small mb-0">Could not load uploaded reports.</p>}
            {!isLoading && !isError && exemplars.length === 0 && (
              <p className="text-muted small mb-0">No reports uploaded yet.</p>
            )}
            {exemplars.length > 0 && (
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Sections</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exemplars.map((exemplar) => (
                    <tr key={exemplar.id}>
                      <td>{exemplar.name}</td>
                      <td>
                        <AppBadge variant={STATUS_VARIANT[exemplar.status] || 'info'}>
                          {exemplar.status}
                        </AppBadge>
                      </td>
                      <td>
                        {exemplar.blueprint?.section_count || 0}
                        {/* A reviewer is about to approve this blueprint for generation. Show what
                            the extractor replaced — uploaded exemplars are often a peer
                            jurisdiction's report, and this is the only place that is visible. */}
                        {exemplar.blueprint?.scrubbed_org_names?.length > 0 && (
                          <div className="small text-warning mt-1">
                            Replaced {exemplar.blueprint.scrubbed_org_names.join(', ')} with your
                            organization name
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        {exemplar.status === 'extracted' && (
                          <Button
                            size="sm"
                            variant="primary"
                            className="me-2"
                            onClick={() => accept(exemplar.id)}
                          >
                            Use blueprint
                          </Button>
                        )}
                        {RETRYABLE.includes(exemplar.status) && (
                          <Button
                            size="sm"
                            variant="light"
                            className="text-primary border me-2"
                            onClick={() => retry(exemplar.id)}
                          >
                            Retry
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="light"
                          className="text-primary border"
                          onClick={() => remove(exemplar.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default DocumentExemplars
